import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const ClubChangeModal = ({ user, onClose }) => {
  const { t } = useLanguage();
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [coachesLoading, setCoachesLoading] = useState(true);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      // Get all SYSTEM_EDITOR users from admin
      const response = await axios.get('/api/admin/users-with-role/SYSTEM_EDITOR');
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
      setError(t('Failed to load coaches. Please try again.'));
    } finally {
      setCoachesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCoach) {
      setError(t('Please select a coach'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('/api/club-change-requests', null, {
        params: {
          customerId: user.id,
          newCoachId: selectedCoach,
          reason: reason.trim() || t('Coach requested club change for customer')
        }
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit club change request:', error);
      setError(error.response?.data?.message || t('Failed to submit request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              {t('Club Change Request Submitted!')}
            </h3>
            <p style={{ color: 'var(--gray-dark)' }}>
              {t('Your request has been sent to the Upline Tab Team for approval.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{t('Request Club Change')}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        <div style={{ padding: '30px' }}>
          <div style={{
            background: 'var(--pale-blue)',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '2px solid var(--primary-blue)'
          }}>
            <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
              {t('Club Change Request for {name}', { name: user?.fullName || user?.firstName || user?.email || '' })}
            </h4>
            <p style={{ fontSize: '14px', color: 'var(--gray-dark)', marginBottom: '10px' }}>
              {t('This request will be sent to the Upline Tab Team for approval. Once approved:')}
            </p>
            <ul style={{ fontSize: '14px', color: 'var(--gray-dark)', marginLeft: '20px' }}>
              <li>{t("Customer's coach will be changed to the selected coach")}</li>
              <li>{t("Customer will be moved under the new coach's hierarchy")}</li>
              <li>{t("Customer's access and programs will remain the same")}</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="currentCoach" className="form-label">
                {t('Current Coach')}
              </label>
              <input
                type="text"
                id="currentCoach"
                className="form-input"
                value={t('Your current coach')}
                disabled={true}
                style={{ backgroundColor: 'var(--gray-light)' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newCoach" className="form-label">
                {t('Select New Coach')} *
              </label>
              {coachesLoading ? (
                <div style={{ padding: '10px', textAlign: 'center', color: 'var(--gray-dark)' }}>
                  {t('Loading coaches...')}
                </div>
              ) : (
                <select
                  id="newCoach"
                  className="form-input"
                  value={selectedCoach}
                  onChange={(e) => setSelectedCoach(e.target.value)}
                  required
                  disabled={loading}
                >
                  <option value="">Select a coach</option>
                  {coaches.map(coach => (
                    <option key={coach.id} value={coach.id}>
                      {coach.fullName} ({coach.email})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="reason" className="form-label">
                {t('Reason for Change (Optional)')}
              </label>
              <textarea
                id="reason"
                className="form-input"
                rows="3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loading}
                placeholder={t('Please provide a reason for the club change request...')}
              />
            </div>

            <div style={{
              background: '#FFF3CD',
              border: '1px solid #FFEAA7',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>{t('Important')}:</strong> {t('This request requires approval from the Upline Tab Team. You will be notified once your request is reviewed.')}
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                {t('Cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !selectedCoach}
              >
                {loading ? t('Submitting...') : t('Submit Request')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClubChangeModal;
